import { PlanLabel } from '@7chairs/types';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useMst } from '../../../models/rootModel';
import { useAnalytics } from '../../../utils/analytics';
import { capitalizeFirstLetter } from '../../../utils/utilsFunctions';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { MainFunnelButtonV2 } from '../../common/v2/MainFunnelButtonV2';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { LoaderDiv } from '../../styledComponent/Boxes.style';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import {
  PickLabelsAnswersWrapper,
  PickLabelsButtonWrapper,
  PickLabelsContainer,
  PickLabelsFadeEffect,
  PickLabelsMainContent,
  PickLabelsSubtitle,
  PickLabelsTitle,
  PickLabelsWrapper,
} from './PickLabels.style';
import { PickLabelsAnswerButton } from './PickLabelsAnswerButton';
import { useIsMobileWebview } from '../../../hooks/useIsMobileWebview.hook';

interface PickLabelsProps extends BaseStepPropsV2 {
  backgroundImageUrl: string;
}

export const PickLabels = observer((props: PickLabelsProps) => {
  const {
    nextStep,
    goToNamedStep,
    isActive,
    ctaButtonTitle,
    previousStep,
    previousStepId,
    backgroundImageUrl,
    title,
    stepName,
    subtitle,
  } = props;

  const searchParams = new URLSearchParams(window.location.search);
  const paramKey = `question_segment`;
  const answerFromQuery = searchParams.get(paramKey);

  const { UserDiscoveryResponse, PlanLabels } = useMst();
  const analytics = useAnalytics();

  const isMobileWebview = useIsMobileWebview();

  const labels =
    isActive && PlanLabels.allPlanLabels.size > 0 && UserDiscoveryResponse.segment
      ? PlanLabels.allPlanLabels.get(UserDiscoveryResponse.segment)
      : [];

  const selectLabel = (label: PlanLabel) => {
    PlanLabels.toggleSelectedPlanLabel(label);
  };

  const onBack = () => {
    if (isMobileWebview && window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'close' }));
      return;
    }

    PlanLabels.clearSelectedPlanLabels();
    if (previousStepId) {
      goToNamedStep(previousStepId);
    } else {
      previousStep();
    }
  };

  const onNextStep = () => {
    analytics.track(`set pickLabels`, {
      stepName,
      planLabelsNames: PlanLabels.selectedPlanLabelsNames,
      planLabelsIds: PlanLabels.selectedPlanLabelsIds,
      segment: UserDiscoveryResponse.segment,
      isMobileWebview,
    });
    nextStep();
  };

  useEffect(() => {
    if (isActive) {
      analytics.track(`view pickLabels`, { stepName, segment: UserDiscoveryResponse.segment, isMobileWebview });

      // CRCL-6319 a sort of a hack to solve scrolling issue 
      document.body.style.overflow = 'hidden';
      return window.scrollTo(0, 0);
    } else {
      document.body.style.overflow = 'visible';
    }
  }, [analytics, isActive, stepName, UserDiscoveryResponse.segment, isMobileWebview]);

  const isButtonDisabled = PlanLabels.selectedPlanLabels.size === 0;

  return (
    <PickLabelsContainer imgDesktop={backgroundImageUrl}>
      <AbsoluteHeader
        {...((!answerFromQuery || isMobileWebview) && { onBack })}
        color="black"
        withLogoOnMobileDisplay
      />
      {labels && labels.length > 0 ? (
        <BaseStepV3 {...props}>
          <PickLabelsWrapper>
            <PickLabelsMainContent>
              <PickLabelsTitle>{title}</PickLabelsTitle>
              <PickLabelsSubtitle>{subtitle}</PickLabelsSubtitle>
              <PickLabelsAnswersWrapper>
                {labels.map((label: PlanLabel) => (
                  <PickLabelsAnswerButton
                    key={label.id}
                    label={label}
                    onClick={selectLabel}
                    isSelected={!!PlanLabels.selectedPlanLabels.get(label.id)}
                  />
                ))}
              </PickLabelsAnswersWrapper>
            </PickLabelsMainContent>

            <PickLabelsFadeEffect />
            <PickLabelsButtonWrapper>
              <MainFunnelButtonV2
                onClick={onNextStep}
                text={ctaButtonTitle}
                disabled={isButtonDisabled}
                id={`mainCta_${capitalizeFirstLetter(props.stepType)}`}
                variant={MainButtonVariant.Gray}
              />
            </PickLabelsButtonWrapper>
          </PickLabelsWrapper>
        </BaseStepV3>
      ) : (
        <LoaderDiv>
          <CircularProgress />
        </LoaderDiv>
      )}
    </PickLabelsContainer>
  );
});
